package com.fantasytenis.web.screens.survivor

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.survivor.entity.*
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen showing a survivor entry's pick history and status.
 */
@Composable
fun SurvivorEntryViewScreen(contestId: String, entryId: String) {
    val scope = rememberCoroutineScope()
    var contest by remember { mutableStateOf<SurvivorContestEntity?>(null) }
    var entryWithPicks by remember { mutableStateOf<SurvivorEntryWithPicksEntity?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // Load data
    LaunchedEffect(entryId) {
        isLoading = true
        error = null

        // Load contest
        DependencyContainer.getSurvivorContestUseCase(contestId).fold(
            onSuccess = { contest = it },
            onFailure = { error = it.message }
        )

        // Load entry with picks
        DependencyContainer.getSurvivorEntryUseCase(entryId).fold(
            onSuccess = {
                entryWithPicks = it
                isLoading = false
            },
            onFailure = {
                error = it.message
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(Colors.Primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button(
                    text = "Back",
                    onClick = { NavigationState.navigate(Route.SurvivorContestDetail(contestId)) },
                    variant = "secondary"
                )
                H2({
                    style {
                        color(Colors.White)
                        fontSize(20.px)
                        fontWeight(700)
                        margin(0.px)
                    }
                }) {
                    Text("Entry Details")
                }
            }
        }

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(800.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.getSurvivorEntryUseCase(entryId).fold(
                            onSuccess = { entryWithPicks = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                entryWithPicks != null && contest != null -> {
                    val entry = entryWithPicks!!.entry
                    val picks = entryWithPicks!!.picks

                    // Entry Status Card
                    EntryStatusCard(entry, contest!!)

                    // Pick History
                    PickHistorySection(picks, contest!!)

                    // Action buttons
                    if (entry.isAlive && contest!!.isActive) {
                        ActionSection(contestId, entryId)
                    } else if (entry.canRebuy && contest!!.variation == SurvivorVariation.REBUY_ALLOWED) {
                        RebuySection(entry, contest!!, entryId, scope, onSuccess = {
                            // Reload entry
                            scope.launch {
                                DependencyContainer.getSurvivorEntryUseCase(entryId).fold(
                                    onSuccess = { entryWithPicks = it },
                                    onFailure = { }
                                )
                            }
                        })
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun EntryStatusCard(entry: SurvivorEntryEntity, contest: SurvivorContestEntity) {
    val statusBgColor = when (entry.status) {
        SurvivorEntryStatus.ALIVE -> rgb(232, 245, 233)
        SurvivorEntryStatus.WINNER, SurvivorEntryStatus.PAID -> rgb(255, 243, 224)
        else -> rgb(255, 235, 238)
    }

    Div({
        style {
            backgroundColor(statusBgColor)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
        }
    }) {
        // Status header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                // Large status icon
                val (icon, iconColor) = when (entry.status) {
                    SurvivorEntryStatus.ALIVE -> "♥" to Colors.Success
                    SurvivorEntryStatus.WINNER, SurvivorEntryStatus.PAID -> "🏆" to Colors.Warning
                    else -> "✖" to Colors.Error
                }
                Span({
                    style {
                        fontSize(32.px)
                        color(iconColor)
                    }
                }) {
                    Text(icon)
                }

                Div {
                    Div({
                        style {
                            fontSize(24.px)
                            fontWeight(700)
                            color(Colors.TextPrimary)
                        }
                    }) {
                        Text(entry.status.displayName)
                    }
                    Div({
                        style {
                            fontSize(14.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text(contest.name)
                    }
                }
            }

            // Rank if applicable
            entry.rank?.let { rank ->
                Div({
                    style {
                        textAlign("right")
                    }
                }) {
                    Div({
                        style {
                            fontSize(12.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("Rank")
                    }
                    Div({
                        style {
                            fontSize(24.px)
                            fontWeight(700)
                            color(Colors.Primary)
                        }
                    }) {
                        Text("#$rank")
                    }
                }
            }
        }

        // Stats grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(100px, 1fr))")
                gap(16.px)
            }
        }) {
            StatBox("Survived", "${entry.survivedRounds} rounds")

            if (contest.variation == SurvivorVariation.STRIKE_SYSTEM) {
                StatBox("Strikes", "${entry.strikesUsed}/${contest.strikeLimit}")
            }

            if (entry.rebuyCount > 0) {
                StatBox("Rebuys", "${entry.rebuyCount}")
            }

            if (entry.payoutAmount > 0) {
                StatBox("Winnings", entry.payoutDisplay, Colors.Success)
            }

            entry.eliminatedInRound?.let { round ->
                StatBox("Eliminated", round.displayName, Colors.Error)
            }
        }
    }
}

@Composable
private fun StatBox(label: String, value: String, valueColor: CSSColorValue? = null) {
    Div({
        style {
            backgroundColor(rgba(255, 255, 255, 0.7))
            borderRadius(8.px)
            padding(12.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(4.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(valueColor ?: Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun PickHistorySection(picks: List<SurvivorPickEntity>, contest: SurvivorContestEntity) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(24.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(16.px)
                margin(0.px)
            }
        }) {
            Text("Pick History")
        }

        if (picks.isEmpty()) {
            Div({
                style {
                    textAlign("center")
                    padding(24.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("No picks made yet")
            }
        } else {
            // Timeline of picks
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                }
            }) {
                picks.sortedByDescending { it.round.ordinal }.forEach { pick ->
                    PickTimelineItem(pick, pick.round == contest.currentRound)
                }
            }
        }
    }
}

@Composable
private fun PickTimelineItem(pick: SurvivorPickEntity, isCurrent: Boolean) {
    val resultBgColor = when (pick.result) {
        SurvivorPickResult.PENDING -> Colors.SurfaceVariant
        SurvivorPickResult.WON, SurvivorPickResult.WALKOVER_WIN -> rgb(232, 245, 233)
        else -> rgb(255, 235, 238)
    }

    val resultColor = when (pick.result) {
        SurvivorPickResult.PENDING -> Colors.TextSecondary
        SurvivorPickResult.WON, SurvivorPickResult.WALKOVER_WIN -> Colors.Success
        else -> Colors.Error
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Stretch)
            gap(16.px)
        }
    }) {
        // Timeline indicator
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                width(60.px)
            }
        }) {
            // Round name
            Div({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(if (isCurrent) Colors.Primary else Colors.TextSecondary)
                    marginBottom(4.px)
                }
            }) {
                Text(pick.round.displayName)
            }

            // Result dot
            Div({
                style {
                    width(12.px)
                    height(12.px)
                    borderRadius(50.percent)
                    backgroundColor(resultColor)
                }
            })
        }

        // Pick card
        Div({
            style {
                flex(1)
                backgroundColor(resultBgColor)
                borderRadius(8.px)
                padding(16.px)
                if (isCurrent) {
                    border(2.px, LineStyle.Solid, Colors.Primary)
                }
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                }
            }) {
                Div {
                    // Player name with seed
                    Div({
                        style {
                            fontWeight(600)
                            fontSize(15.px)
                            color(Colors.TextPrimary)
                        }
                    }) {
                        pick.displaySeed?.let { Text("$it ") }
                        Text(pick.playerName)
                    }

                    // Matchup
                    Div({
                        style {
                            fontSize(13.px)
                            color(Colors.TextSecondary)
                            marginTop(4.px)
                        }
                    }) {
                        Text("vs ")
                        pick.opponentDisplaySeed?.let { Text("$it ") }
                        Text(pick.opponentName ?: "TBD")
                    }

                    // Match score if available
                    pick.matchScore?.let { score ->
                        Div({
                            style {
                                fontSize(12.px)
                                color(Colors.TextSecondary)
                                marginTop(4.px)
                            }
                        }) {
                            Text(score)
                        }
                    }
                }

                // Result badge
                ResultBadge(pick.result)
            }
        }
    }
}

@Composable
private fun ResultBadge(result: SurvivorPickResult) {
    val (bgColor, textColor, text) = when (result) {
        SurvivorPickResult.PENDING -> Triple(Colors.Gray200, Colors.TextSecondary, "Pending")
        SurvivorPickResult.WON -> Triple(rgb(232, 245, 233), Colors.Success, "Won")
        SurvivorPickResult.LOST -> Triple(rgb(255, 235, 238), Colors.Error, "Lost")
        SurvivorPickResult.WALKOVER_WIN -> Triple(rgb(232, 245, 233), Colors.Success, "W/O Win")
        SurvivorPickResult.WALKOVER_LOSS -> Triple(rgb(255, 235, 238), Colors.Error, "W/O Loss")
        SurvivorPickResult.NO_PICK -> Triple(rgb(255, 235, 238), Colors.Error, "No Pick")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(12.px)
            fontWeight(600)
            padding(4.px, 12.px)
            borderRadius(12.px)
        }
    }) {
        Text(text)
    }
}

@Composable
private fun ActionSection(contestId: String, entryId: String) {
    Div({
        style {
            backgroundColor(rgb(232, 245, 233))
            borderRadius(12.px)
            padding(24.px)
            textAlign("center")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(8.px)
                margin(0.px)
            }
        }) {
            Text("Make Your Pick")
        }
        P({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
                marginBottom(16.px)
                margin(0.px)
                marginTop(8.px)
            }
        }) {
            Text("Select a player for the current round")
        }
        Button(
            text = "Select Player",
            onClick = {
                NavigationState.navigate(Route.SurvivorPickSelection(contestId, entryId))
            },
            variant = "primary"
        )
    }
}

@Composable
private fun RebuySection(
    entry: SurvivorEntryEntity,
    contest: SurvivorContestEntity,
    entryId: String,
    scope: kotlinx.coroutines.CoroutineScope,
    onSuccess: () -> Unit
) {
    var isRebuying by remember { mutableStateOf(false) }
    var rebuyError by remember { mutableStateOf<String?>(null) }

    Div({
        style {
            backgroundColor(rgb(255, 243, 224))
            borderRadius(12.px)
            padding(24.px)
            textAlign("center")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(8.px)
                margin(0.px)
            }
        }) {
            Text("Eliminated - Rebuy Available")
        }
        P({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
                marginBottom(16.px)
                margin(0.px)
                marginTop(8.px)
            }
        }) {
            Text("You've been eliminated but can rebuy to continue playing")
        }

        rebuyError?.let { err ->
            P({
                style {
                    color(Colors.Error)
                    fontSize(14.px)
                    marginBottom(16.px)
                }
            }) {
                Text(err)
            }
        }

        Button(
            text = if (isRebuying) "Processing..." else "Rebuy for ${contest.rebuyFeeDisplay}",
            onClick = {
                scope.launch {
                    isRebuying = true
                    rebuyError = null
                    DependencyContainer.rebuySurvivorEntryUseCase(entryId).fold(
                        onSuccess = {
                            isRebuying = false
                            onSuccess()
                        },
                        onFailure = { e ->
                            rebuyError = e.message
                            isRebuying = false
                        }
                    )
                }
            },
            variant = "primary"
        )
    }
}
